import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Calculator } from "lucide-react";

// Percentuais oficiais da plataforma (mesmos do backend):
// Nível 1: 10% · Nível 2: 1,5% · Nível 3: 1% · Nível 4: 0,5% · Nível 5: 0,2%
const LEVELS = [
  { level: 1, rate: 0.10, label: "10%", name: "Nível 1 (Direto)", max: 50, initial: 5 },
  { level: 2, rate: 0.015, label: "1,5%", name: "Nível 2", max: 100, initial: 10 },
  { level: 3, rate: 0.01, label: "1%", name: "Nível 3", max: 200, initial: 20 },
  { level: 4, rate: 0.005, label: "0,5%", name: "Nível 4", max: 400, initial: 40 },
  { level: 5, rate: 0.002, label: "0,2%", name: "Nível 5", max: 800, initial: 80 },
];

const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2)}`;

const EarningsSimulator = () => {
  const [merchants, setMerchants] = useState(2);
  const [levelUsers, setLevelUsers] = useState(LEVELS.map((l) => l.initial));
  const [avgOrderValue, setAvgOrderValue] = useState(50);
  const [ordersPerMonth, setOrdersPerMonth] = useState(10);

  const merchantReferralEarnings = merchants * 79.60; // 40% de R$ 199

  const levelEarnings = LEVELS.map((l, i) => levelUsers[i] * ordersPerMonth * avgOrderValue * l.rate);
  const totalLevels = levelEarnings.reduce((a, b) => a + b, 0);
  const totalMonthly = merchantReferralEarnings + totalLevels;
  const totalYearly = totalMonthly * 12;

  const setLevel = (i) => (value) => {
    setLevelUsers((arr) => arr.map((v, idx) => (idx === i ? value[0] : v)));
  };

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <Calculator className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Simulador de Ganhos
            </h2>
            <p className="text-xl text-gray-600">
              Calcule seu potencial de ganhos com a rede multinível — até o 5º nível
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Inputs */}
            <Card className="p-8">
              <h3 className="font-heading text-2xl font-bold mb-6">Configure Sua Rede</h3>

              <div className="space-y-5">
                <div>
                  <Label className="text-base font-semibold mb-2 block">
                    Lojistas Indicados: {merchants}
                  </Label>
                  <Slider
                    value={[merchants]}
                    onValueChange={(value) => setMerchants(value[0])}
                    min={0}
                    max={20}
                    step={1}
                    className="mt-2"
                    data-testid="sim-slider-merchants"
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Ganho: R$ {(merchants * 79.60).toFixed(2)}/mês
                  </p>
                </div>

                {LEVELS.map((l, i) => (
                  <div key={l.level}>
                    <Label className="text-base font-semibold mb-2 block">
                      Usuários {l.name}: {levelUsers[i]}
                    </Label>
                    <Slider
                      value={[levelUsers[i]]}
                      onValueChange={setLevel(i)}
                      min={0}
                      max={l.max}
                      step={1}
                      className="mt-2"
                      data-testid={`sim-slider-level-${l.level}`}
                    />
                    <p className="text-sm text-gray-600 mt-1">
                      Comissão: {l.label} das vendas
                    </p>
                  </div>
                ))}

                <div>
                  <Label className="text-base font-semibold mb-2 block">
                    Valor Médio do Pedido: R$ {avgOrderValue}
                  </Label>
                  <Slider
                    value={[avgOrderValue]}
                    onValueChange={(value) => setAvgOrderValue(value[0])}
                    min={20}
                    max={200}
                    step={10}
                    className="mt-2"
                    data-testid="sim-slider-avg-order"
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold mb-2 block">
                    Pedidos por Pessoa/Mês: {ordersPerMonth}
                  </Label>
                  <Slider
                    value={[ordersPerMonth]}
                    onValueChange={(value) => setOrdersPerMonth(value[0])}
                    min={1}
                    max={30}
                    step={1}
                    className="mt-2"
                    data-testid="sim-slider-orders"
                  />
                </div>
              </div>
            </Card>

            {/* Resultados */}
            <Card className="p-8 bg-gradient-to-br from-secondary to-purple-700 text-white">
              <h3 className="font-heading text-2xl font-bold mb-6">Seus Ganhos Potenciais</h3>

              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                  <p className="text-sm opacity-90 mb-1">Bônus de Indicação de Lojistas</p>
                  <p className="text-2xl font-bold" data-testid="sim-result-merchants">
                    {fmtBRL(merchantReferralEarnings)}
                  </p>
                  <p className="text-xs opacity-80 mt-0.5">
                    {merchants} lojistas × R$ 79,60
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {LEVELS.map((l, i) => (
                    <div key={l.level} className="bg-white/10 backdrop-blur-sm p-3 rounded-xl">
                      <p className="text-xs opacity-90 mb-0.5">Comissões {l.name}</p>
                      <p className="text-xl font-bold" data-testid={`sim-result-level-${l.level}`}>
                        {fmtBRL(levelEarnings[i])}
                      </p>
                      <p className="text-[10px] opacity-80 mt-0.5">
                        {levelUsers[i]} pessoas × {ordersPerMonth} pedidos × {l.label}
                      </p>
                    </div>
                  ))}
                  <div className="bg-white/10 backdrop-blur-sm p-3 rounded-xl flex flex-col justify-center">
                    <p className="text-xs opacity-90 mb-0.5">Total da Rede (5 níveis)</p>
                    <p className="text-xl font-bold" data-testid="sim-result-network">{fmtBRL(totalLevels)}</p>
                  </div>
                </div>

                <div className="border-t border-white/20 pt-4">
                  <div className="mb-3">
                    <p className="text-sm opacity-90 mb-1">Total Mensal</p>
                    <p className="text-5xl font-bold" data-testid="sim-result-monthly">
                      {fmtBRL(totalMonthly)}
                    </p>
                  </div>

                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
                    <p className="text-sm opacity-90 mb-1">Projeção Anual</p>
                    <p className="text-3xl font-bold" data-testid="sim-result-yearly">
                      {fmtBRL(totalYearly)}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              * Valores estimados. Os ganhos reais dependem da atividade da sua rede.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarningsSimulator;
